import { useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../card/Card'
import { Select, SelectOption } from './Select'
import { TimezoneSelect } from './TimezoneSelect'
import { Monitor, Sun, Moon } from 'lucide-react'
import { PREFERENCE_LANGUAGES, type PreferenceLanguage } from '@airclou/utils'

export type Theme = 'light' | 'dark' | 'system'

export interface PreferencesCardProps {
  language: string
  timezone: string
  theme: Theme
  onLanguageChange: (value: string) => void
  onTimezoneChange: (value: string) => void
  onThemeChange: (value: Theme) => void
  disabled?: boolean
  className?: string
  /** Suppress the in-card "Preferences" title (e.g. when the page renders its own section header above the card). */
  hideHeader?: boolean
  /**
   * Shown under the controls when the last save failed. The provider has
   * already put the value back; without this line the control simply snaps
   * back and the person keeps clicking it. Pass the translated message, or
   * null when there is nothing to say.
   */
  saveError?: string | null
  /**
   * Hide the Timezone field. For an app that does not yet render its dates
   * through `useTimezone` — showing the control there is worse than omitting
   * it, because changing it appears to do something and does not. Drop the
   * flag once the app's dates actually honour the preference.
   */
  hideTimezone?: boolean
  /**
   * Translated copy. `packages/ui` carries no i18n of its own — components here
   * take their strings as props (see `FormPageLayout`) so a shared component
   * never has to depend on a consumer's i18n instance. Every field is optional
   * and falls back to English, so an untranslated app is unaffected.
   *
   * The language *names* are deliberately not included: "English" and
   * "中文 (Chinese)" are each written in their own language by convention, so
   * translating them would make the list harder to use, not easier.
   */
  labels?: {
    title?: string
    theme?: string
    language?: string
    timezone?: string
    themeSystem?: string
    themeLight?: string
    themeDark?: string
    /** Rendered under Theme when it is 'system'. Receives the resolved theme's label. */
    systemHint?: (resolvedThemeLabel: string) => string
  }
}

/**
 * The languages this card offers, re-exported so a consumer deciding which
 * value to display cannot drift from the list the Select actually renders.
 * Dashboard needs it: with no stored preference it shows the language i18next
 * resolved rather than the stored default, and a value outside this list would
 * leave the Select with no matching option.
 *
 * The list itself lives in `@airclou/utils` because the Auth service validates
 * stored preferences against it, and a backend cannot import this package.
 */
export { PREFERENCE_LANGUAGES, type PreferenceLanguage } from '@airclou/utils'

/**
 * Each language written in its own language, which is the convention for a
 * language picker — translating "中文" into English would make the list harder
 * to use, not easier. Typed by `PreferenceLanguage` so adding a code to
 * `PREFERENCE_LANGUAGES` without a label is a compile error rather than a blank
 * option: the constant exists precisely so the two cannot drift, and deriving
 * the options from it is what makes that true rather than merely intended.
 */
const LANGUAGE_LABELS: Record<PreferenceLanguage, string> = {
  en: 'English',
  zh: '中文 (Chinese)',
}

const languageOptions: SelectOption[] = PREFERENCE_LANGUAGES.map((value) => ({
  value,
  label: LANGUAGE_LABELS[value],
}))

/**
 * Which colour scheme the OS is asking for. Returns the caller's own labels so
 * the hint reads in one language rather than two.
 */
function getSystemThemeLabel(darkLabel: string, lightLabel: string): string {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? darkLabel : lightLabel
  }
  return lightLabel
}

export function PreferencesCard({
  language,
  timezone,
  theme,
  onLanguageChange,
  onTimezoneChange,
  onThemeChange,
  disabled = false,
  className = '',
  hideHeader = false,
  hideTimezone = false,
  saveError = null,
  labels,
}: PreferencesCardProps) {
  const text = {
    title: labels?.title ?? 'Preferences',
    theme: labels?.theme ?? 'Theme',
    language: labels?.language ?? 'Language',
    timezone: labels?.timezone ?? 'Timezone',
    themeSystem: labels?.themeSystem ?? 'System',
    themeLight: labels?.themeLight ?? 'Light',
    themeDark: labels?.themeDark ?? 'Dark',
  }

  const localisedThemeOptions = useMemo<SelectOption[]>(
    () => [
      { value: 'system', label: text.themeSystem, icon: Monitor },
      { value: 'light', label: text.themeLight, icon: Sun },
      { value: 'dark', label: text.themeDark, icon: Moon },
    ],
    [text.themeSystem, text.themeLight, text.themeDark],
  )

  // Deliberately NOT memoized. It reads `matchMedia` live, and the deps that
  // would key a memo are stable forever for any caller that passes no `labels`
  // — helpdesk and playbook — so the hint would freeze at whatever the OS
  // scheme was on first render and still read "Currently using: Light" after
  // the user flips their OS to dark with the page open. Dashboard escaped that
  // only by accident, because its inline `labels` object gets a new identity
  // each render and forced a recompute. A memo that is stale where its deps are
  // stable and inert where they are not earns nothing; this is one `matchMedia`
  // read on a component that re-renders only when preferences change.
  const systemHint = (() => {
    if (theme !== 'system') return null
    const resolved = getSystemThemeLabel(text.themeDark, text.themeLight)
    return labels?.systemHint
      ? labels.systemHint(resolved)
      : `Currently using: ${resolved} (system preference)`
  })()

  return (
    <Card className={className}>
      {!hideHeader && (
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">{text.title}</CardTitle>
        </CardHeader>
      )}
      <CardContent className={hideHeader ? 'space-y-6 pt-6' : 'space-y-6'}>
        {/* Theme */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium">{text.theme}</label>
          <Select
            value={theme}
            onChange={(v) => onThemeChange(v as Theme)}
            options={localisedThemeOptions}
            disabled={disabled}
            showSelectedIcon
            minWidth="200px"
          />
          {systemHint && (
            <p className="text-xs text-muted-foreground">{systemHint}</p>
          )}
        </div>

        {/* Language */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium">{text.language}</label>
          <Select
            value={language}
            onChange={onLanguageChange}
            options={languageOptions}
            disabled={disabled}
            minWidth="200px"
          />
        </div>

        {/* Timezone */}
        {!hideTimezone && (
          <div className="space-y-1.5">
            <label className="block text-sm font-medium">{text.timezone}</label>
            <TimezoneSelect
              value={timezone}
              onChange={onTimezoneChange}
              disabled={disabled}
              showSelectedIcon
              minWidth="280px"
            />
          </div>
        )}

        {saveError && (
          <p role="alert" className="text-sm text-destructive">
            {saveError}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

export default PreferencesCard
